import moment from 'moment';
import {
  IOwnershipGraph,
  normalizeEntityName,
  relationBand,
} from 'utils/beneficialOwnershipGraph';
import {
  beneficialOwnershipWorkflowData,
  buildBeneficialOwnershipReportData,
} from 'utils/beneficialOwnershipReportData';

// The one-page visual summary of a saved Beneficial Ownership ticket. Every
// section is read from the same saved report the full PDF prints, so its
// counts and wording agree with that report: nothing is generated or
// rewritten, only selected, ranked and grouped. What the page shows follows
// what the report holds -- a run with no owners, no flagged parties or no
// connections at all gets a page built from what it does have.

/** The Saved Work download option that picks the visual over the report. */
export const BO_VISUAL_DOWNLOAD_OPTION = 'BO_VISUAL';

export type VisualTone = 'high' | 'medium' | 'low' | 'info';

export interface IVisualText {
  title: string;
  body: string;
  tone: VisualTone;
}

export interface IVisualKpi {
  value: number;
  label: string;
  detail: string;
  tone: VisualTone;
}

export interface IVisualFinding {
  title: string;
  body: string;
  level: string;
  tone: VisualTone;
}

export interface IVisualNetworkItem {
  name: string;
  detail: string;
  flagged: boolean;
  /**
   * How serious the item is, for a visual that grades its items (risk
   * findings). Left out, a flagged item is drawn as high and the rest plain.
   */
  tone?: VisualTone;
}

/**
 * The page's fixed wording. A visual for another tool (Export and
 * Transshipment Risk) names its own sections; left out, every label is the
 * Beneficial Ownership one.
 */
export interface IVisualLabels {
  kicker: string;
  subtitle: string;
  strip: [string, string, string, string];
  networkTitle: string;
  /** What a mapped item is called: [one, many], e.g. ['party', 'parties']. */
  networkItem: [string, string];
  networkSource: string;
  moreGroup: string;
  /** The one grid a small network is drawn as, e.g. 'Connections'. */
  compactGroup: string;
  exposureTitle: string;
  /** What an exposure item is called: [one, many]. */
  exposureItem: [string, string];
  exposureNone: string;
  exposureList: string;
  exposureEmpty: string;
  exposureMore: string;
  jurisdictions: string;
  summary: string;
  footer: string;
  documentTitle: string;
  documentAuthor: string;
}

export type VisualGroupKey =
  | 'owners'
  | 'parents'
  | 'vessels'
  | 'holdings'
  | 'people'
  | 'investors'
  | 'other';

export interface IVisualNetworkGroup {
  key: VisualGroupKey;
  title: string;
  /** Every member, flagged first. The renderer shows what fits. */
  items: IVisualNetworkItem[];
  flaggedCount: number;
}

export interface IBeneficialOwnershipVisualData {
  ticketName: string;
  entityName: string;
  /** The two-line verdict in the header: the subject, then its network. */
  verdict: { subject: string; network: string; tone: VisualTone };
  subject: IVisualText;
  ownershipResult: IVisualText;
  analysis: IVisualText;
  report: IVisualText;
  /** Only the counts the report has; empty when it has none. */
  kpis: IVisualKpi[];
  /** Every finding, highest risk first. The renderer shows what fits. */
  findings: IVisualFinding[];
  network: {
    /** The badge under the subject: its ownership state. */
    badge: { label: string; tone: VisualTone };
    groups: IVisualNetworkGroup[];
    total: number;
    flagged: number;
  };
  /** Every sanctioned or flagged party in the network, owners first. */
  exposure: IVisualNetworkItem[];
  assessment: { headline: string; note: string; tone: VisualTone };
  jurisdictions: string[];
  /** The start of the report's sanctions summary; empty when it has none. */
  sanctions: string;
  /** The page's wording, when it is not a Beneficial Ownership visual. */
  labels?: Partial<IVisualLabels>;
}

const asArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const text = (value: any): string =>
  typeof value === 'string'
    ? value.replace(/\s+/g, ' ').trim()
    : typeof value === 'number'
    ? String(value)
    : '';

const count = (n: number): string => n.toLocaleString('en-US');

const plural = (n: number, one: string, many: string): string =>
  `${count(n)} ${n === 1 ? one : many}`;

// The agent's schema says confirmed|possible|none, but it is model-written and
// drifts; these all mean "nothing found". Same list the Saved Work page uses.
const CLEAN_FLAGS = [
  '',
  'none',
  'no',
  'not confirmed',
  'not available',
  'n/a',
  'na',
  'null',
  'unknown',
  'false',
  'no sanctions',
  'not sanctioned',
  'nothing found',
  'no match',
  'no matches',
  'clear',
  'cleared',
  'negative',
];

export const isFlagged = (value: any): boolean =>
  CLEAN_FLAGS.indexOf(text(value).toLowerCase()) === -1;

type SanctionsVerdict = 'confirmed' | 'possible' | 'clean';

const sanctionsVerdict = (flag: string): SanctionsVerdict => {
  const lower = flag.toLowerCase();
  if (!isFlagged(lower)) return 'clean';
  if (lower.indexOf('confirm') !== -1) return 'confirmed';
  // "possible", "potential" and anything else that is still a flag.
  return 'possible';
};

const RISK_RANK: { [level: string]: number } = {
  high: 3,
  medium: 2,
  low: 1,
  info: 0,
};

const levelOf = (finding: any): string =>
  typeof finding === 'string' ? '' : text(finding?.risk_level).toLowerCase();

const rankOf = (finding: any): number => {
  const rank = RISK_RANK[levelOf(finding)];
  return typeof rank === 'number' ? rank : -1;
};

const toneOfLevel = (level: string): VisualTone =>
  level === 'high'
    ? 'high'
    : level === 'medium'
    ? 'medium'
    : level === 'low'
    ? 'low'
    : 'info';

const TONE_RANK: { [tone in VisualTone]: number } = {
  info: 0,
  low: 1,
  medium: 2,
  high: 3,
};

const worstTone = (...tones: VisualTone[]): VisualTone =>
  tones.reduce((worst, tone) =>
    TONE_RANK[tone] > TONE_RANK[worst] ? tone : worst,
  );

const findingTitle = (finding: any, index: number): string =>
  typeof finding === 'string'
    ? `Finding ${index + 1}`
    : text(finding?.title) ||
      text(finding?.risk_type) ||
      `Finding ${index + 1}`;

const findingBody = (finding: any): string =>
  typeof finding === 'string'
    ? text(finding)
    : text(finding?.finding) ||
      text(finding?.description) ||
      text(finding?.summary);

/** The text up to the end of its first sentence, or all of it when short. */
export const firstSentence = (value: string): string => {
  const clean = text(value);
  const match = clean.match(/^(.+?[.!?])(\s|$)/);
  return match ? match[1] : clean;
};

/** Up to `n` sentences from the start of the text. */
const sentences = (value: string, n: number): string => {
  let rest = text(value);
  const out: string[] = [];
  while (rest && out.length < n) {
    const next = firstSentence(rest);
    out.push(next);
    rest = rest.slice(next.length).trim();
  }
  return out.join(' ');
};

// ---- Network --------------------------------------------------------------

// Not connections: another name for the subject or one of its connections.
const ALIAS_RELATIONS = ['formerly known as', 'also known as', 'alias', 'aka'];

const isAliasRelation = (relation: string): boolean => {
  const lower = relation.toLowerCase();
  return ALIAS_RELATIONS.some((alias) => lower.indexOf(alias) !== -1);
};

// A vessel is named by its type or by how it relates to the subject. Company
// names alone ("Alpha Shipping") never decide it.
const VESSEL_TYPE =
  /\b(vessel|ship|tanker|carrier|bulker|container ship|lng carrier)\b/i;
const VESSEL_RELATION =
  /\b(vessel|charter|chartered|ship manager of|registered owner of)\b/i;

const GROUP_TITLE: { [key in VisualGroupKey]: string } = {
  owners: 'Owners & controllers',
  parents: 'Parents & shareholders in the network',
  vessels: 'Vessels',
  holdings: 'Subsidiaries & affiliates',
  people: 'Officers & directors',
  investors: 'Investors',
  other: 'Other connections',
};

const groupKeyOf = (
  relation: string,
  type: string,
): Exclude<VisualGroupKey, 'owners'> => {
  if (VESSEL_TYPE.test(type) || VESSEL_RELATION.test(relation)) {
    return 'vessels';
  }
  const band = relationBand(relation);
  if (band === 'owners') return 'parents';
  if (band === 'holdings') return 'holdings';
  if (band === 'people') return 'people';
  if (band === 'investors') return 'investors';
  return 'other';
};

const relationOf = (row: any): string =>
  text(row?.relation) ||
  text(row?.relationship_to_subject) ||
  text(row?.relationship);

/** "51" → "51%"; words ("majority") are kept as written. */
const shareOf = (value: any): string => {
  const share = text(value);
  if (!share || /^(n\/a|null|unknown|not specified)$/i.test(share)) return '';
  return /^\d+(\.\d+)?$/.test(share) ? `${share}%` : share;
};

const buildNetwork = (
  owners: any[],
  associated: any[],
  sanctionsConnections: any[],
  graph: IOwnershipGraph | undefined,
  subjectNames: string[],
): { groups: IVisualNetworkGroup[]; total: number; flagged: number } => {
  // The list each sanctioned name is on, from the report's sanctions screen.
  const listedOn: { [key: string]: string } = Object.create(null);
  sanctionsConnections.forEach((row: any) => {
    const key = normalizeEntityName(row?.sanctioned_entity);
    if (key && listedOn[key] === undefined) {
      listedOn[key] =
        [text(row?.sanctions_list), text(row?.sanctions_program)]
          .filter(Boolean)
          .join(' · ') || 'Sanctions match';
    }
  });

  const skip = subjectNames.map(normalizeEntityName).filter(Boolean);
  const seen: { [key: string]: boolean } = Object.create(null);
  const members: { [key: string]: IVisualNetworkItem[] } = Object.create(null);
  const order: VisualGroupKey[] = [];

  const add = (row: any, isOwner: boolean) => {
    const name = text(row?.name);
    const key = normalizeEntityName(name);
    const relation = relationOf(row) || (isOwner ? 'owner' : '');
    if (!key || seen[key] || skip.indexOf(key) !== -1) return;
    if (isAliasRelation(relation)) return;
    seen[key] = true;

    const node = graph?.nodes?.[key];
    const flagged =
      isFlagged(row?.sanctions_flag) ||
      listedOn[key] !== undefined ||
      Boolean(node?.flagged);
    // An owner's stake, when the report gives one ("51", "51%", "majority").
    const stake = isOwner ? shareOf(row?.ownership_percentage) : '';
    const detail = flagged
      ? [relation, stake, listedOn[key] || 'Sanctions flag']
          .filter(Boolean)
          .join(' · ')
      : [relation, stake, text(node?.country)].filter(Boolean).join(' · ');
    // owners[] is the report's own ownership finding. associated_entities is
    // a flat list that mixes in second- and third-layer discoveries (a
    // "shareholder" of the subject's parent), so its owner-type rows are the
    // network's parents, never the subject's owners.
    const group: VisualGroupKey = isOwner
      ? 'owners'
      : groupKeyOf(relation, text(node?.type));
    if (!members[group]) {
      members[group] = [];
      order.push(group);
    }
    members[group].push({ name: name, detail: detail, flagged: flagged });
  };

  owners.forEach((row) => add(row, true));
  associated.forEach((row) => add(row, false));
  // A sanctioned party the sanctions screen tied to the subject belongs on the
  // map even when the connection lists left it out.
  sanctionsConnections.forEach((row: any) => {
    const via = text(row?.connected_via);
    add(
      {
        name: row?.sanctioned_entity,
        relation:
          text(row?.relationship) ||
          (via ? `via ${via}` : 'sanctions connection'),
        sanctions_flag: 'listed',
      },
      false,
    );
  });

  const groups = order.map((key) => {
    const items = members[key];
    const flaggedItems = items.filter((item) => item.flagged);
    return {
      key: key,
      title: GROUP_TITLE[key],
      items: flaggedItems.concat(items.filter((item) => !item.flagged)),
      flaggedCount: flaggedItems.length,
    };
  });
  // The subject's owners lead; then the groups with the most flagged
  // members, then the largest. "Other" goes last whatever its size.
  groups.sort((a, b) => {
    if (a.key === 'owners' || b.key === 'owners') {
      return a.key === 'owners' ? -1 : 1;
    }
    if (a.key === 'other' || b.key === 'other') {
      return a.key === 'other' ? 1 : -1;
    }
    return b.flaggedCount - a.flaggedCount || b.items.length - a.items.length;
  });

  return {
    groups: groups,
    total: groups.reduce((sum, group) => sum + group.items.length, 0),
    flagged: groups.reduce((sum, group) => sum + group.flaggedCount, 0),
  };
};

// ---- Sources ---------------------------------------------------------------

/** The sites the report cites most, e.g. "opensanctions.org, state.gov, +6". */
const sourceSummary = (links: any[]): string => {
  const hits: { [host: string]: number } = Object.create(null);
  const hosts: string[] = [];
  links.forEach((link: any) => {
    const match = text(link?.url).match(/^https?:\/\/([^/?#]+)/i);
    if (!match) return;
    const host = match[1].toLowerCase().replace(/^www\./, '');
    if (hits[host] === undefined) {
      hits[host] = 0;
      hosts.push(host);
    }
    hits[host] += 1;
  });
  if (!hosts.length) {
    return links.length ? 'Cited in the full report' : 'No sources saved';
  }
  const ranked = hosts
    .map((host, index) => ({ host: host, index: index }))
    .sort((a, b) => hits[b.host] - hits[a.host] || a.index - b.index)
    .map((item) => item.host);
  const shown = ranked.slice(0, 2);
  const rest = ranked.length - shown.length;
  return shown.join(', ') + (rest > 0 ? `, +${count(rest)}` : '');
};

// ---- Verdict and assessment ------------------------------------------------

const verdictOf = (
  verdict: SanctionsVerdict,
  flagged: number,
  highFindings: number,
): IBeneficialOwnershipVisualData['verdict'] => {
  const subject =
    verdict === 'confirmed'
      ? 'Sanctions confirmed'
      : verdict === 'possible'
      ? 'Possible sanctions match'
      : 'Not sanctioned';
  const subjectTone: VisualTone =
    verdict === 'confirmed'
      ? 'high'
      : verdict === 'possible'
      ? 'medium'
      : 'low';
  if (flagged > 0) {
    return {
      subject: subject,
      network:
        verdict === 'confirmed'
          ? plural(flagged, 'flagged connection', 'flagged connections')
          : 'High risk via connected parties',
      tone: 'high',
    };
  }
  if (highFindings > 0) {
    return {
      subject: subject,
      network: plural(highFindings, 'high-risk finding', 'high-risk findings'),
      tone: worstTone(subjectTone, 'medium'),
    };
  }
  return {
    subject: subject,
    network: 'No flagged connections',
    tone: subjectTone,
  };
};

const assessmentOf = (
  verdict: SanctionsVerdict,
  flagged: number,
  highFindings: number,
): IBeneficialOwnershipVisualData['assessment'] => {
  if (verdict === 'confirmed') {
    return {
      headline: 'Sanctions exposure confirmed',
      note: 'Escalate for compliance review. Enhanced due diligence required.',
      tone: 'high',
    };
  }
  if (verdict === 'possible') {
    return {
      headline: 'Possible sanctions concern',
      note: 'Enhanced due diligence and manual verification required.',
      tone: flagged > 0 ? 'high' : 'medium',
    };
  }
  if (flagged > 0) {
    return {
      headline: 'Sanctioned parties in the network',
      note: 'The subject is not sanctioned, but parties connected to it are. Review those connections before proceeding.',
      tone: 'high',
    };
  }
  if (highFindings > 0) {
    return {
      headline: 'Elevated risk',
      note: 'No sanctions match, but the report rates some findings high risk. Review them before proceeding.',
      tone: 'medium',
    };
  }
  return {
    headline: 'No sanctions concern found',
    note: 'No sanctions match was found for the subject or its connections.',
    tone: 'low',
  };
};

const reportMomentOf = (ticket: any) => {
  const date = moment(ticket?.updatedAt || ticket?.createdAt || undefined);
  return date.isValid() ? date : moment();
};

export const buildBeneficialOwnershipVisualData = (
  ticket: any,
  preparedBy: string,
): IBeneficialOwnershipVisualData => {
  const report = buildBeneficialOwnershipReportData(ticket, preparedBy);
  const result =
    ticket?.beneficialOwnership ||
    beneficialOwnershipWorkflowData(ticket).result ||
    {};
  const owners = asArray(report.owners);
  const keyFindings = asArray(report.keyFindings);
  const associated = asArray(report.associatedEntities);
  const sources = asArray(report.supportingLinks);
  const graph = report.ownershipGraph;

  const entityName = text(report.entityName) || 'Unnamed entity';
  const reportName = text(result?.normalized_subject_name);
  const matchedName =
    reportName &&
    normalizeEntityName(reportName) !== normalizeEntityName(entityName)
      ? reportName
      : '';
  const subjectNode = graph?.nodes?.[graph.subject];
  const country =
    text(report.country) && report.country !== 'Not provided'
      ? text(report.country)
      : '';

  const verdictKind = sanctionsVerdict(text(report.sanctionsFlag));
  const highFindings = keyFindings.filter((f) => levelOf(f) === 'high').length;
  const network = buildNetwork(
    owners,
    associated,
    asArray(result?.sanctions_connections),
    graph,
    [entityName, matchedName],
  );
  const vessels = network.groups.filter((g) => g.key === 'vessels')[0];
  const ownersGroup = network.groups.filter((g) => g.key === 'owners')[0];
  // Flagged owners are counted under owners; this is the rest of the network.
  const flaggedConnected = network.flagged - (ownersGroup?.flaggedCount || 0);

  // ---- The strip under the header
  const subjectFacts = [
    matchedName ? `Searched as ${entityName}` : '',
    text(subjectNode?.type),
    text(subjectNode?.country) || country,
  ].filter(Boolean);
  const subject: IVisualText = {
    title: matchedName || entityName,
    body: subjectFacts.length
      ? subjectFacts.join(' · ')
      : firstSentence(text(result?.general_description)) ||
        'No further detail was saved for the subject.',
    tone: 'info',
  };

  const ownerNames = owners.map((owner) => text(owner?.name)).filter(Boolean);
  const ownershipResult: IVisualText = ownerNames.length
    ? {
        title: `${plural(ownerNames.length, 'owner', 'owners')} identified`,
        body:
          ownerNames.slice(0, 3).join(', ') +
          (ownerNames.length > 3
            ? ` and ${count(ownerNames.length - 3)} more`
            : ''),
        tone: 'info',
      }
    : {
        title: 'Ownership could not be verified',
        body: 'No shareholders, directors or parent company were confirmed in the sources reviewed.',
        tone: 'medium',
      };

  const analysis: IVisualText = report.secondLayer
    ? {
        title: 'Second-layer search enabled',
        body: 'Connected entities were researched one level further, mapping sanctions exposure across the network.',
        tone: 'info',
      }
    : {
        title: 'Direct connections only',
        body: 'The subject’s own owners and connections were researched.',
        tone: 'info',
      };

  const reportMoment = reportMomentOf(ticket);
  const reportInfo: IVisualText = {
    title: reportMoment.format('MMM D, YYYY · HH:mm'),
    body: `Prepared by ${text(preparedBy) || 'BITE Beneficial Ownership'}`,
    tone: 'info',
  };

  // ---- Counts (the same counts the full report prints)
  const allKpis: IVisualKpi[] = [
    {
      value: owners.length,
      label: 'Owners found',
      detail: owners.length
        ? ownersGroup?.flaggedCount
          ? `${count(ownersGroup.flaggedCount)} with a sanctions flag`
          : 'Identified in the report'
        : 'Beneficial owners unknown',
      tone: owners.length ? 'info' : 'high',
    },
    {
      value: keyFindings.length,
      label: 'Key findings',
      detail: highFindings
        ? `${count(highFindings)} rated high risk`
        : keyFindings.length
        ? 'None rated high risk'
        : 'None saved',
      tone: 'info',
    },
    {
      value: associated.length,
      label: 'Connected entities',
      detail: flaggedConnected
        ? `Incl. ${plural(
            flaggedConnected,
            'flagged party',
            'flagged parties',
          )}`
        : vessels
        ? `Incl. ${plural(vessels.items.length, 'vessel', 'vessels')}`
        : associated.length
        ? 'None flagged'
        : 'None named in the report',
      tone: 'info',
    },
    {
      value: sources.length,
      label: 'Sources',
      detail: sourceSummary(sources),
      tone: 'info',
    },
  ];
  // A count of nothing is left off. The owners tile is the exception while
  // anything else is counted: no owners found is itself a finding.
  const counted = allKpis.slice(1).filter((kpi) => kpi.value > 0);
  const kpis = counted.length
    ? [allKpis[0]].concat(counted)
    : allKpis[0].value > 0
    ? [allKpis[0]]
    : [];

  // ---- Findings and jurisdictions
  const ranked = keyFindings
    .map((finding, index) => ({ finding: finding, index: index }))
    .sort((a, b) => rankOf(b.finding) - rankOf(a.finding) || a.index - b.index);
  const findings = ranked.map(({ finding, index }) => {
    const level = levelOf(finding);
    return {
      title: findingTitle(finding, index),
      body: findingBody(finding),
      level: level,
      tone: toneOfLevel(level),
    };
  });

  const jurisdictions = asArray(
    result?.jurisdiction_risk?.high_risk_jurisdictions,
  )
    .map(text)
    .filter(Boolean);

  return {
    ticketName: text(report.ticketName),
    entityName: entityName,
    verdict: verdictOf(verdictKind, network.flagged, highFindings),
    subject: subject,
    ownershipResult: ownershipResult,
    analysis: analysis,
    report: reportInfo,
    kpis: kpis,
    findings: findings,
    network: {
      badge: ownerNames.length
        ? {
            label: plural(ownerNames.length, 'owner found', 'owners found'),
            tone: 'info',
          }
        : { label: 'Owner unverified', tone: 'medium' },
      groups: network.groups,
      total: network.total,
      flagged: network.flagged,
    },
    exposure: network.groups.reduce(
      (all: IVisualNetworkItem[], group) =>
        all.concat(group.items.filter((item) => item.flagged)),
      [],
    ),
    assessment: assessmentOf(verdictKind, network.flagged, highFindings),
    jurisdictions: jurisdictions,
    sanctions: sentences(text(report.sanctionsSummary), 3),
  };
};

/**
 * Whether the visual has anything to show: an owner, a connected party or a
 * sanctions connection to map. Without one the visual would only restate the
 * report, so the download offers the full report instead.
 */
export const hasNetworkToMap = (
  data: IBeneficialOwnershipVisualData,
): boolean => data.network.total > 0;

export const canBuildBeneficialOwnershipVisual = (ticket: any): boolean =>
  hasNetworkToMap(buildBeneficialOwnershipVisualData(ticket, ''));

/** Why the visual is unavailable, for the disabled download option. */
export const NO_NETWORK_TO_MAP =
  'No network to map. The report names no owners or connected parties, so download the full report instead.';
